import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Button, Tooltip } from "tombac";
import { LimitsIcon } from "tombac-icons";
import { LimitSchema } from "../../components/Limits/limitsClient";
import { LimitUsage } from "../../components/Limits/LimitUsage";
import { StyledLimits } from "../../components/Limits/StyledLimits";
import { TrialLimitsSummary } from "../../components/Trial";
import useOutsideClick from "../../useOutsideClick";
import {
  AccessType,
  LimitsSharedWithProduct,
  LimitUsages,
  useLimitSchemasQuery,
  useMeProductAccessQuery,
  useUserProduct,
} from "../legolandApi";
import { useLegoland } from "../LegolandContext";
import { Dot, IconButton } from "./IconButton";
import { TrackingFunction } from "../navbar/LegolandNavbarTypes";

type Limits = Record<string, unknown>;

interface LegolandNavbarProps {
  track: TrackingFunction;
}

export interface LimitWithUsage {
  limit: unknown;
  schema?: LimitSchema;
  sharedWith?: string;
  usage?: unknown;
}

function createLimitsWithUsage(
  limits: Limits,
  schema: LimitSchema[],
  usage: LimitUsages,
  limitsSharedWith?: LimitsSharedWithProduct[]
) {
  const limitsWithUsage: LimitWithUsage[] = Object.entries(limits)
    .filter(([_, limit]) => limit !== null)
    .map(([limitName, limit]) => {
      const limitSchema = schema.find((s) => s.name === limitName);
      const limitUsage = usage[limitName];
      const sharedWith = limitsSharedWith?.find(({ names }) =>
        names.includes(limitName)
      )?.productId;

      return { limit, schema: limitSchema, sharedWith, usage: limitUsage };
    })
    .filter(
      (l) => l.schema?.customizable === true && l.schema?.visible === true
    );

  return limitsWithUsage.length > 0 ? limitsWithUsage : undefined;
}

export const LegolandNavbarLimits: FC<LegolandNavbarProps> = ({ track }) => {
  const { legolandUiUrl, productId } = useLegoland();

  const product = useUserProduct({ productId });
  const schemaQuery = useLimitSchemasQuery(
    { productId },
    { enabled: Boolean(productId) } // Don't fetch schemas when not within a product
  );
  const accessQuery = useMeProductAccessQuery();

  const productAccess = accessQuery.data?.access;
  const { limits, limitsSharedWith, usage } = productAccess ?? {};
  const schemas = schemaQuery.data?.schemas;

  const [limitsWithUsage, setLimitsWithUsage] = useState<
    LimitWithUsage[] | undefined
  >();

  useEffect(() => {
    if (!limits || !schemas || !usage) return;
    setLimitsWithUsage(
      createLimitsWithUsage(limits, schemas, usage, limitsSharedWith)
    );
  }, [limits, limitsSharedWith, schemas, usage]);

  const [showLimits, setShowLimits] = useState(false);
  const [limitsRef, setLimitsRef] = useState<HTMLDivElement | null>(null);
  const closeLimits = useCallback(() => setShowLimits(false), [setShowLimits]);
  useOutsideClick(limitsRef, closeLimits);

  return productAccess && limitsWithUsage && product ? (
    <StyledLimits.Container
      ref={setLimitsRef}
      onClick={() => {
        track("page_click", {
          specificProps: {
            component_name: "header",
            component_sub_name: `limits-button`,
          },
        });
        setShowLimits((x) => !x);
      }}
    >
      <Tooltip
        content="Usage&nbsp;limits"
        placement="bottom"
        size="s"
        variant="inverted"
      >
        {({ close, open, ref }) => (
          <IconButton
            onMouseEnter={() => !showLimits && open()}
            onMouseLeave={close}
            ref={ref}
            shape="circle"
            variant="flat"
          >
            <LimitsIcon />
            {productAccess.type === AccessType.Evaluation && (
              <Dot breakpoint={0} variant="alert" />
            )}
          </IconButton>
        )}
      </Tooltip>

      {showLimits && (
        <StyledLimits.Popup.Container>
          <StyledLimits.Popup.Header>
            <StyledLimits.Popup.Title>Usage limits</StyledLimits.Popup.Title>
            <StyledLimits.Popup.Subtitle>
              {product?.displayName}
            </StyledLimits.Popup.Subtitle>
          </StyledLimits.Popup.Header>
          {productAccess?.type === AccessType.Evaluation && (
            <StyledLimits.Popup.Item>
              <TrialLimitsSummary
                productAccess={productAccess}
                activeProduct={productId!}
              />
            </StyledLimits.Popup.Item>
          )}
          {limitsWithUsage.map(
            (l) =>
              l.schema && <LimitUsage key={l.schema.name} limitWithUsage={l} />
          )}
          {productAccess?.type === AccessType.Evaluation ? (
            <StyledLimits.Popup.Footer isTrial>
              <div>
                <StyledLimits.Popup.FooterTitle>
                  Do you want to upgrade and gain access to more features?
                </StyledLimits.Popup.FooterTitle>
                <StyledLimits.Popup.FooterDescription>
                  Contact us
                </StyledLimits.Popup.FooterDescription>
              </div>
              <Button
                $flex="none"
                $marginLeft="2sp"
                as="a"
                href={`${legolandUiUrl}/dashboard/contact`}
                size="s"
                target="_blank"
                variant="alert"
              >
                Contact us
              </Button>
            </StyledLimits.Popup.Footer>
          ) : (
            <StyledLimits.Popup.Footer>
              <StyledLimits.Popup.FooterTitle>
                Do you need more?
              </StyledLimits.Popup.FooterTitle>
              <StyledLimits.Popup.FooterDescription>
                Contact your distributor to change your limits
              </StyledLimits.Popup.FooterDescription>
            </StyledLimits.Popup.Footer>
          )}
        </StyledLimits.Popup.Container>
      )}
    </StyledLimits.Container>
  ) : null;
};
